import {HTMLAttributes, ButtonHTMLAttributes, ReactElement, ReactNode, useRef, useEffect, forwardRef, PropsWithChildren, useId} from 'react'
import Link from 'next/link'
import {cx} from '@local/utils/cx'
import {LinkInput} from '@local/types'
import {tv, type VariantProps} from 'tailwind-variants'

import {resolveLink} from '@local/utils/rewrite'
import {routes, locale} from '@local/stores/dictionaries/route'

export const buttonStyles = tv({
	slots: {
		base: [
			'relative appearance-none cursor-pointer font-sans text-center overflow-clip',
			'text-12 uppercase tracking-doubleWide leading-none font-medium',
			'disabled:pointer-events-none',
		],
		icon: ['[&_svg]:w-18 [&_svg]:h-18 mr-10'],
		serif: ['font-serif'],
	},
	variants: {
		variant: {
			none: 'inline-block',
			primary: {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-50 bg-grey-5 !text-white border border-grey-5 px-50 pt-21 pb-19',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-white hover/btn:!text-grey-5 hover/btn:border-white',
					'focus-visible:bg-white focus-visible:!text-grey-5 focus-visible:border-white',
					'disabled:bg-grey-1 disabled:text-grey-1 disabled:border-grey-1 disabled:cursor-not-allowed',
				),
			},
			'primary-icon': {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-44 bg-grey-5 !text-white border border-grey-5 aspect-square',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-white hover/btn:!text-grey-5 hover/btn:border-white',
					'focus-visible:bg-white focus-visible:!text-grey-5 focus-visible:border-white',
				),
			},
			secondary: {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-50 bg-white border border-white px-50 pt-21 pb-19 text-grey-5',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-grey-5 hover/btn:text-white hover/btn:border-grey-5',
					'focus-visible:bg-grey-5 focus-visible:text-white focus-visible:border-grey-5',
				),
			},
			'secondary-icon': {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-44 bg-white text-grey-5 border border-white aspect-square',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-grey-5 hover/btn:text-white hover/btn:border-grey-5',
					'focus-visible:bg-grey-5 focus-visible:text-white focus-visible:border-grey-5',
				),
			},
			icon: {
				base: cx(
					'inline-flex items-center justify-center text-center',
					'uppercase text-8 bg-none border-grey-1 border px-18 py-18 rounded outline-none aspect-square',
					'transition-[background,color,opacity,border] duration-300 ease-quart-out',
					'hover:bg-grey-5 hover:text-white hover:border-grey-5',
					'focus-visible:outline-none focus-visible:bg-grey-5 focus-visible:text-white focus-visible:border-grey-5',
				),
			},
			classicUnderlined: {
				base: 'inline-block underline',
			},
			underlined: {
				base: cx(
					'inline-block focus-visible:outline-none',
					'after:absolute after:bottom-0 after:left-0',
					'after:w-full after:h-1 after:bg-current',
					'after:scale-x-0 after:hover:scale-x-100 after:focus-visible:scale-x-100 after:origin-left',
					'after:transition-transform after:duration-500 after:ease-quart-out after:transform-gpu',
				),
			},
			'primary-transparent': {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-50 bg-transparent text-white border border-grey-1 px-50 pt-21 pb-19',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-white hover/btn:!text-grey-5',
					'focus-visible:bg-white focus-visible:!text-grey-5',
				),
			},
			'secondary-transparent': {
				base: cx(
					'inline-flex items-center justify-center overflow-clip rounded outline-none group/btn',
					'h-50 bg-transparent text-black border border-grey-1 px-50 pt-21 pb-19',
					'ease-quart-out duration-500 transition-[background-color,color,border]',
					'hover/btn:bg-grey-5 hover/btn:!text-white hover/btn:border-grey-5',
					'focus-visible:bg-grey-5 focus-visible:!text-white focus-visible:border-grey-5',
				),
			},
		},
		active: {
			true: '',
		},
		error: {
			true: 'border-red text-grey-5 bg-white',
		},
		disabled: {
			true: 'border-grey-1 text-grey-1 bg-white !pointer-events-none',
		},
		loading: {
			true: 'cursor-wait',
		},
		loaded: {
			true: 'cursor-default',
		},
		small: {
			true: 'h-44 pt-19 pb-17 px-24 uppercase font-medium',
		},
		icon: {
			true: '',
		},
		serif: {
			true: '',
		},
		border: {
			true: 'border-grey-5 hover/btn:border-grey-5 focus-visible:border-grey-5',
		},
	},
	compoundVariants: [
		{
			variant: 'underlined',
			active: true,
			class: 'after:scale-x-100 after:hover:scale-x-0 after:origin-right focus-visible:after:scale-x-0 focus-visible:after:origin-right',
		},
		{
			variant: ['primary', 'primary-icon', 'primary-transparent'],
			active: true,
			class: 'bg-white !text-grey-5 border-white',
		},
		{
			variant: ['secondary', 'secondary-icon', 'secondary-transparent'],
			active: true,
			class: 'bg-grey-5 text-white border-grey-5',
		},
		{
			variant: ['primary', 'secondary'],
			icon: true,
			class: {
				base: 'h-44 py-18 px-24 text-8 uppercase font-medium tracking-widest',
			},
		},
		{
			variant: ['primary', 'secondary'],
			serif: true,
			class: '[&_.btn-text]:mr-5',
		},
		{
			variant: ['primary', 'secondary'],
			serif: true,
			small: true,
			class: 'h-44 pt-19 pb-17 px-24 [&_.btn-text]:mr-10',
		},
	],
})

type ButtonVariants = VariantProps<typeof buttonStyles>

export type ButtonProps = HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> & {
	as?,
	children?: ReactNode,
	className?: string,
	active?: boolean,
	linkInput?: LinkInput,
	href?: string,
	target?: string,
	disabled?: boolean,
	loading?: boolean,
	loaded?: boolean,
	prefetch?: boolean,
	shallow?: boolean,
	type?: ButtonHTMLAttributes<HTMLButtonElement>['type'],
	/** `aria-label` for a11y. */
	label?: string,
	icon?: ReactNode,
	serif?: ReactNode,
	border?: boolean,
	small?: boolean,
	gtmEvent?: object,
	// styleProps?: ButtonVariants
} & ButtonVariants

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement, PropsWithChildren<ButtonProps>>(({
	as = 'button',
	target,
	children,
	className,
	onClick,
	linkInput,
	href,
	active,
	disabled,
	loading,
	loaded,
	label,
	prefetch = false,
	shallow,
	icon,
	serif,
	border,
	small = false,
	...props
}: ButtonProps, ref): ReactElement => {
	const Tag = as
	const key = useId()

	let resolvedLink
	if (linkInput) {
		// resolvedLink = useResolveLink(linkInput)
		resolvedLink = resolveLink(linkInput, locale.current, routes.current.rewrites)
		label = label || linkInput.label
	} else if (href) {
		resolvedLink = resolveLink(href, locale.current, routes.current.rewrites)
		href = resolvedLink.href
	}

	if (resolvedLink) {
		href = resolvedLink.href
		active = active || resolvedLink.active
	}

	const {base, icon: iconClassName, serif: serifClassName} = buttonStyles({
		...props,
		active,
		disabled,
		loading,
		loaded,
		icon: Boolean(icon),
		serif: Boolean(serif),
		small,
		border,
	})

	const classNames = [
		base(),
		className,
	]

	if (icon) {
		children = (
			<>
				<span className={cx('btn-icon', iconClassName())}>{icon}</span>
				<span className={cx('btn-text')}>{children}</span>
			</>
		)
	}

	if (serif) {
		children = (
			<>
				<span className={cx('btn-text')}>{children}</span>
				<span className={cx('btn-serif', serifClassName())}>{serif}</span>
			</>
		)
	}

	if (href) {
		const _target = target || href.indexOf('mailto:') === 0 || href.indexOf('http') === 0 ? '_blank' : undefined
		return (
			<Link
				key={key}
				ref={ref}
				href={href}
				target={_target}
				onClick={onClick}
				className={cx(classNames)}
				aria-label={label}
				prefetch={prefetch}
				shallow={shallow}
				{...props}>
				{children || label}
			</Link>
		)
	} else {
		return (
			<Tag key={key} ref={ref} className={cx(classNames)} disabled={disabled} aria-label={label} onClick={onClick} {...props}>
				{children || label}
			</Tag>
		)
	}
})
